import { useParams, Link } from 'react-router-dom';
import { useAuthorDetails } from '@/hooks/useAuthorDetails';
import { FollowAuthorButton } from '@/components/FollowAuthorButton';
import { BookCard } from '@/components/BookCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, BookOpen, ArrowLeft, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { ReportButton } from '@/components/ReportButton';
import { normalizeOpenLibraryId } from '@/lib/searchUtils';
import { groupBooksBySeries } from '@/lib/seriesUtils';
import { AuthorSeriesProgress } from '@/components/AuthorSeriesProgress';

// Generate deterministic UUID v5 for author bios based on author name
function generateAuthorBioId(authorName: string): string {
  // Using a fixed namespace UUID for author bios
  const AUTHOR_BIO_NAMESPACE = '6ba7b810-9dad-11d1-80b4-00c04fd430c8';
  
  // Simple deterministic UUID generation (basic implementation)
  // In production, you'd use a proper UUID v5 library
  const normalizedName = authorName.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();
  const hash = normalizedName.split('').reduce((acc, char) => {
    return ((acc << 5) - acc) + char.charCodeAt(0);
  }, 0);
  
  // Create a UUID-like string based on the hash
  const hex = Math.abs(hash).toString(16).padStart(32, '0');
  return `${hex.substring(0, 8)}-${hex.substring(8, 12)}-4${hex.substring(13, 16)}-${hex.substring(16, 20)}-${hex.substring(20, 32)}`;
}

const BOOKS_PER_PAGE = 20;

export default function AuthorPage() {
  const { authorName } = useParams<{ authorName: string }>();
  const decodedName = authorName ? decodeURIComponent(authorName) : '';
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'all' | 'in-library' | 'not-in-library'>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const offset = (page - 1) * BOOKS_PER_PAGE;
  const { data: authorData, isLoading, error } = useAuthorDetails(decodedName, BOOKS_PER_PAGE, offset);
  
  const displayBooks = filter === 'in-library' 
    ? authorData?.localBooks || []
    : filter === 'not-in-library'
    ? authorData?.externalBooks || []
    : [...(authorData?.localBooks || []), ...(authorData?.externalBooks || [])];
  
  const totalPages = authorData ? Math.ceil(authorData.totalBooks / BOOKS_PER_PAGE) : 0;

  const handleRefreshAuthorInfo = async () => {
    if (!decodedName) return;
    
    setIsRefreshing(true);
    try {
      // Invalidate the query cache to force a fresh fetch from external sources
      await queryClient.invalidateQueries({ queryKey: ['author-details', decodedName] });
      
      toast({
        title: "Author info refreshed",
        description: "Fetching updated information from external sources...",
      });
    } catch (error) {
      console.error('Error refreshing author info:', error);
      toast({
        title: "Refresh failed",
        description: "Could not refresh author information. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleBookClick = async (book: any) => {
    // If it's a local book, navigate directly
    if (!book.isExternal) {
      window.location.href = `/book/${book.id}`;
      return;
    }

    try {
      // For external books, check if it exists or create it
      const normalizedId = normalizeOpenLibraryId(book.open_library_key || book.id);
      if (!normalizedId) {
        throw new Error('Invalid book identifier');
      }

      // Check for existing book
      const { data: existingBooks } = await supabase
        .from('books')
        .select('id, published_year, open_library_id')
        .or(`open_library_id.eq.${normalizedId},open_library_id.eq./works/${normalizedId}`)
        .limit(5);

      const matchingBook = existingBooks?.find(existing => {
        const yearMatch = !book.published_year || !existing.published_year || 
                          existing.published_year === book.published_year;
        const olidMatch = existing.open_library_id === normalizedId || 
                         existing.open_library_id === `/works/${normalizedId}`;
        return olidMatch && yearMatch;
      });

      if (matchingBook) {
        window.location.href = `/book/${matchingBook.id}`;
        return;
      }

      // Create new book
      const { data: newBook, error } = await supabase
        .from('books')
        .insert({
          open_library_id: normalizedId,
          title: book.title || 'Untitled',
          authors: [decodedName],
          published_year: book.published_year,
          cover_url: book.cover_url,
          synopsis: book.synopsis,
          source: 'openlibrary',
          language: 'en',
          enrichment_status: 'pending',
        })
        .select('id')
        .single();

      if (error) throw error;
      window.location.href = `/book/${newBook?.id}`;
    } catch (err) {
      console.error('Error opening book', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'There was an error opening the book details.',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <Skeleton className="h-8 w-48 mb-4" />
        <div className="grid gap-6">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (error || !authorData) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">Author not found or error loading data.</p>
            <Link to="/search">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Back button */}
      <Link to="/discover">
        <Button variant="ghost" size="sm" className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Discover
        </Button>
      </Link>

      {/* Author Header */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            {/* Author Image */}
            {authorData.imageUrl && (
              <div className="flex-shrink-0">
                <img 
                  src={authorData.imageUrl} 
                  alt={authorData.authorName}
                  className="w-32 h-32 md:w-40 md:h-40 rounded-lg object-cover shadow-lg"
                />
              </div>
            )}
            
            <div className="flex-1 space-y-4">
              <div>
                <h1 className="text-3xl font-bold mb-2">{authorData.authorName}</h1>
                
                {/* Author Metadata */}
                <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mb-3">
                  {authorData.birthYear && (
                    <span className="font-medium">Born: {authorData.birthYear}</span>
                  )}
                  {authorData.nationality && (
                    <span className="font-medium">{authorData.nationality}</span>
                  )}
                  {authorData.authorAverageRating && (
                    <span className="font-medium">
                      Avg Rating: {Number(authorData.authorAverageRating).toFixed(1)} ⭐
                    </span>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <BookOpen className="h-4 w-4" />
                    <span>{authorData.totalBooks} books</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    <span>{authorData.followerCount} followers</span>
                  </div>
                  {authorData.readerCount > 0 && (
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      <span>{authorData.readerCount} readers</span>
                    </div>
                  )}
                </div>
              </div>

              {authorData.bio && (
                <div>
                  <p className="text-muted-foreground leading-relaxed">{authorData.bio}</p>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex-1">
                      {authorData.bioSource && (
                        <p className="text-xs text-muted-foreground">
                          {authorData.bioSource === 'ai_generated' 
                            ? 'Bio generated by AI' 
                            : `Source: ${authorData.bioSource}`}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRefreshAuthorInfo}
                        disabled={isRefreshing}
                        className="text-xs"
                      >
                        <RefreshCw className={`h-3 w-3 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
                        Refresh Info
                      </Button>
                      <ReportButton
                        contentType="author_bio"
                        contentId={generateAuthorBioId(authorData.authorName)}
                        contentPreview={`Bio for ${authorData.authorName}: ${authorData.bio.substring(0, 100)}...`}
                        variant="ghost"
                        size="sm"
                        className="text-xs"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Awards Section */}
              {authorData.awards && (
                <div className="pt-2">
                  <h3 className="text-sm font-semibold mb-1">Notable Awards</h3>
                  <p className="text-sm text-muted-foreground">{authorData.awards}</p>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <FollowAuthorButton authorName={authorData.authorName} size="lg" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Top Books Section */}
      {((authorData.mostPopularBooks && authorData.mostPopularBooks.length > 0) || 
        (authorData.highestRatedBooks && authorData.highestRatedBooks.length > 0)) && (
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Most Popular Books */}
          {authorData.mostPopularBooks && authorData.mostPopularBooks.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4">Most Popular on Platform</h3>
                <div className="space-y-3">
                  {authorData.mostPopularBooks.map((book) => (
                    <div 
                      key={book.id}
                      className="flex gap-3 p-2 hover:bg-muted/50 rounded-lg cursor-pointer transition-colors"
                      onClick={() => handleBookClick(book)}
                    >
                      {book.cover_url && (
                        <img 
                          src={book.cover_url} 
                          alt={book.title}
                          className="w-12 h-16 object-cover rounded"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{book.title}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                          <span>{book.rating_count} ratings</span>
                          {book.average_rating && (
                            <span>• {book.average_rating.toFixed(1)} ⭐</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Highest Rated Books */}
          {authorData.highestRatedBooks && authorData.highestRatedBooks.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4">Highest Rated</h3>
                <div className="space-y-3">
                  {authorData.highestRatedBooks.map((book) => (
                    <div 
                      key={book.id}
                      className="flex gap-3 p-2 hover:bg-muted/50 rounded-lg cursor-pointer transition-colors"
                      onClick={() => handleBookClick(book)}
                    >
                      {book.cover_url && (
                        <img 
                          src={book.cover_url} 
                          alt={book.title}
                          className="w-12 h-16 object-cover rounded"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{book.title}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                          <span className="font-semibold text-foreground">
                            {book.average_rating?.toFixed(1)} ⭐
                          </span>
                          <span>• {book.rating_count} ratings</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Author Series Progress */}
      {authorData.seriesGroups && authorData.seriesGroups.length > 0 && (
        <AuthorSeriesProgress 
          authorName={authorData.authorName}
          seriesGroups={authorData.seriesGroups}
        />
      )}

      {/* Series Section */}
      {authorData.seriesGroups && authorData.seriesGroups.length > 0 && (
        <div className="mb-6 space-y-6">
          {authorData.seriesGroups.map((series) => (
            <Card key={series.series_name}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold">{series.series_name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {series.book_count} {series.book_count === 1 ? 'book' : 'books'}
                    </p>
                  </div>
                  <Badge variant="secondary">Series</Badge>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {series.books.map((book) => (
                    <div key={book.id} className="relative">
                      <BookCard
                        book={{
                          id: book.id,
                          title: book.title,
                          authors: [authorData.authorName],
                          coverUrl: book.cover_url,
                          publishedYear: book.published_year,
                        }}
                      onClick={() => handleBookClick(book)}
                      />
                      {(book.series_position || book.detected_series_position) && (
                        <Badge 
                          variant="default" 
                          className="absolute top-2 left-2 text-xs"
                        >
                          #{book.series_position || book.detected_series_position}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Books Section */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Books by {authorData.authorName}</h2>
            <div className="flex gap-2">
              <Badge 
                variant={filter === 'all' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilter('all')}
              >
                All ({authorData.totalBooks})
              </Badge>
              {authorData.localBooks.length > 0 && (
                <Badge 
                  variant={filter === 'in-library' ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setFilter('in-library')}
                >
                  In Library ({authorData.localBooks.length})
                </Badge>
              )}
              {authorData.externalBooks.length > 0 && (
                <Badge 
                  variant={filter === 'not-in-library' ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setFilter('not-in-library')}
                >
                  Discover ({authorData.externalBooks.length})
                </Badge>
              )}
            </div>
          </div>

          {displayBooks.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No books found for this author.
            </p>
          ) : (
            <>
              {authorData.externalBooks.length > 0 && filter !== 'in-library' && (
                <div className="mb-4 p-3 bg-muted/50 rounded-lg border border-border/50">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">Note:</span> Books marked with "Discover" are from external sources and not yet in your library. Click on them to add.
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {displayBooks.map((book) => (
                  <div key={book.id} className="relative">
                    <BookCard
                      book={{
                        id: book.id,
                        title: book.title,
                        authors: [authorData.authorName],
                        coverUrl: book.cover_url,
                        publishedYear: book.published_year,
                      }}
                      onClick={() => handleBookClick(book)}
                    />
                    {book.isExternal && (
                      <Badge 
                        variant="secondary" 
                        className="absolute top-2 right-2 text-xs bg-primary/90 text-primary-foreground"
                      >
                        Discover
                      </Badge>
                    )}
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          variant={page === pageNum ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPage(pageNum)}
                          className="w-10"
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
